import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"

import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { checkPermission } from "shared/imports/permissionFunctions"
import { isCanadaRegion, isInternational } from "shared/imports/regionConstants"

import type { CampaignFormValues } from "@/pages/campaign-form/types"
import type {
    Campaign,
    CampaignMessageVariation,
    CampaignType as TCampaignType,
    DjangIOEnum,
    PermissionType,
    Placeholder,
    SafedSearch,
    QuorumDataTypesWithValue,
    CustomField,
} from "@/types/djangio"
import type { QueryFunctionContext } from "@tanstack/react-query"
import { api } from "@/api"
import { LINK_SHARE_LINK_TYPE } from "@/pages/campaign-form/components/campaigns/shareALink/Message"
import { getDefaultMessageDummy } from "@/pages/campaign-form/helpers"
import {
    TEAM_PERMISSION_EVERYONE,
    TEAM_PERMISSION_LIMITED_TEAMS,
} from "@/components/TeamPermissioningFormSection/TeamPermissioningFormSection"

const { Bill, BillTerm } = DjangIO.app.bill.models
const { EmailTemplate } = DjangIO.app.userdata.outbox.models.email_template
const { ActionCenterSettings, GrassrootsRegistrationPage } = DjangIO.app.grassroots.models
const { IssueManagement, Project, Team } = DjangIO.app.userdata.models
const { Regulation } = DjangIO.app.reg.models
const { ActionCenterBackgroundStyle } = DjangIO.app.grassroots.enums
const { DraftOptions, MessageVariationsType } = DjangIO.app.grassroots.campaign.types
const { Campaign: CampaignModel } = DjangIO.app.grassroots.campaign.models

export const campaignsKeys = {
    all: [{ _model: "campaign" }] as const,
    detail: (campaignId: string) => [{ ...campaignsKeys.all[0], campaignId }] as const,
    filters: (filters: ConstructorParameters<typeof URLSearchParams>[0]) =>
        [...campaignsKeys.all, { filters }] as const,
}

const fetchCampaign = async ({ queryKey }: QueryFunctionContext<ReturnType<(typeof campaignsKeys)["detail"]>>) => {
    const [{ campaignId }] = queryKey
    return api
        .get({
            resourceUri: CampaignModel.resourceUriFromId(campaignId),
            params: {
                decode_enums: "false",
                count: "false",
                dehydrate_extra: "multi_action_children,personalized_campaign_pages,child_campaign_stats",
            },
        })
        .then((resp) => resp.json()) as Promise<Campaign>
}

const listCampaigns = (params: ConstructorParameters<typeof URLSearchParams>[0] = {}) =>
    api
        .get({
            model: DjangIO.app.grassroots.campaign.models.Campaign,
            params: params,
        })
        .then((resp) => resp.json())

export const useCampaign = (campaignId: string) =>
    useQuery({
        queryKey: campaignsKeys.detail(campaignId),
        queryFn: fetchCampaign,
        enabled: Boolean(campaignId),
        select: useCallback((campaign) => campaignTransformer(campaign), []),
        staleTime: Infinity,
    })

interface UseReorderCampaignsParams {
    filters: ConstructorParameters<typeof URLSearchParams>[0]
    successCallback?: (data) => void
}

export const formatReorderCampaigns = (data) => ({
    paginationDetails: data?.meta,
    campaigns:
        data?.objects?.map((campaign: Campaign) => ({
            id: campaign.id,
            title: !!campaign.name ? campaign.name : `Campaign ${campaign.id}`,
            order: campaign.order,
        })) ?? [],
})

export const useReorderCampaigns = ({ filters = "", successCallback }: UseReorderCampaignsParams) =>
    useQuery({
        queryKey: campaignsKeys.filters(filters),
        queryFn: () =>
            listCampaigns(filters).then((data) => {
                if (successCallback) successCallback(data)
                return data
            }),
        select: useCallback((data) => formatReorderCampaigns(data), [filters]),
        initialData: { campaigns: [] },
        refetchOnWindowFocus: false,
    })

export const listCampaignTypeItemsForNewCampaignForm = () => {
    const CampaignType = DjangIO.app.grassroots.campaign.types.CampaignType as unknown as DjangIOEnum<TCampaignType>
    const PermissionTypeModel = DjangIO.app.models.PermissionType as unknown as DjangIOEnum<PermissionType>

    return CampaignType.items().filter((item) => {
        const disableCanadianGrassroots = isCanadaRegion ? item.canadian_grassroots_enabled : true
        const hasNeededPermissions = item.permission_enum.every((permissionValue) =>
            checkPermission(PermissionTypeModel.by_value(permissionValue)),
        )
        const enablePacCampaign =
            item.value !== DjangIO.app.grassroots.campaign.types.CampaignType.donate.value || isFeatureEnabled("ff_pac")

        if (item.value === DjangIO.app.grassroots.campaign.types.CampaignType.multi_action.value) return false

        return (
            item.is_enabled_for_campaign_form && enablePacCampaign && hasNeededPermissions && disableCanadianGrassroots
        )
    })
}

export const campaignTransformer = (
    campaign: Campaign,
): Omit<Campaign, keyof CampaignFormValues> & CampaignFormValues => {
    const regulationIds = campaign.tagged_regulations?.map((regulation) =>
        parseInt(Regulation.idFromResourceUri(regulation) as any),
    )
    const billIds = campaign.tagged_bills?.map((bill) => parseInt(Bill.idFromResourceUri(bill) as any))
    const issueIds = campaign.projects?.map(
        (issue) =>
            parseInt(IssueManagement.idFromResourceUri(issue) as any) ||
            parseInt(Project.idFromResourceUri(issue) as any),
    )

    const emailTemplateId = campaign.email_template
        ? // @ts-ignore
          parseInt(EmailTemplate.idFromResourceUri(campaign.email_template as any) as any)
        : null

    const registrationPageId = campaign.registration_page
        ? GrassrootsRegistrationPage.idFromResourceUri(campaign.registration_page)
        : campaign.action_center_dict?.default_registration_page_id

    const policyAreaId = campaign.policy_area ? BillTerm.idFromResourceUri(campaign.policy_area) : null

    const [confirmationEmailFromAddressUsername, confirmationEmailFromAddressDomain] = campaign.conf_email_from_address
        ? campaign.conf_email_from_address.split("@")
        : ["", ""]

    const { SocialMediaType } = DjangIO.app.grassroots.enums

    const socialMediaPlatforms = //overrides the default value of [1, 2, 3, 4] as we only have 3 social media platforms on the new campaign form
        campaign.social_media_platforms.length > 3 ? [SocialMediaType.facebook.value] : campaign.social_media_platforms

    const thankYouMessage =
        campaign.thank_you_message || `<p>Thank you for supporting the "${campaign.name || "Save the Whales"}"!</p>`

    campaign.message_groups?.forEach((messageGroup) => {
        messageGroup.uses_default_member_list = !isInternational && messageGroup.standard_options_search ? true : false
        messageGroup.has_custom_email_targets = messageGroup.custom_email_targets?.length > 0 ? true : false

        messageGroup.custom_officials_searches =
            isInternational && !messageGroup.custom_officials_searches?.length
                ? [messageGroup.standard_options_search]
                : messageGroup.custom_officials_searches

        const aiNotEnabledAndTypeAutoGenerated =
            !isFeatureEnabled("ff_ngg_campaigns_ai") &&
            messageGroup.message_variations_type === MessageVariationsType.auto_generated.value

        if (aiNotEnabledAndTypeAutoGenerated) {
            messageGroup.message_variations_type = MessageVariationsType.custom_written.value
        }
    })

    const usesDefaultMemberList = !isInternational ? campaign.uses_default_member_list : false

    const primaryTargetsSavedSearches =
        isInternational && !campaign.primary_targets_saved_searches?.length
            ? [campaign.default_targets_saved_search]
            : campaign.primary_targets_saved_searches

    const ui_custom_targets_enabled = campaign.custom_target?.length > 0

    const multi_action_children = []

    if (campaign.campaign_type === DjangIO.app.grassroots.campaign.types.CampaignType.multi_action.value) {
        campaign._extra.multi_action_children.forEach((child: Campaign) => {
            multi_action_children.push(campaignTransformer(child))
        })
    }

    const limitedToTeamsIds = campaign.limited_to_teams?.map((team) => Number(Team.idFromResourceUri(team as string)))

    return {
        ...campaign,
        tagged_regulations: regulationIds,
        tagged_bills: billIds,
        projects: issueIds,
        email_template: emailTemplateId,
        should_send_confirmation_email: Boolean(campaign.email_template),
        policy_area: policyAreaId,
        confirmation_email_from_address_username: confirmationEmailFromAddressUsername,
        confirmation_email_from_address_domain: confirmationEmailFromAddressDomain,
        registration_page: registrationPageId,
        full_bleed_header_type: String(campaign.full_bleed_header_type),
        social_media_platforms: socialMediaPlatforms,
        full_bleed_header_text_is_left_aligned: false,
        navigation_bar_type: campaign?.navigation_bar_type.toString(),
        thank_you_message: thankYouMessage,
        background_style_type: campaign?.background_style_type
            ? campaign.background_style_type.toString()
            : ActionCenterBackgroundStyle.org_design.value.toString(),
        background_color_override: campaign?.background_color_override || "#ffffff",
        gamification_points_mode: String(campaign.gamification_points_mode),
        limits_participants: campaign?.participants_saved_searches?.length > 0,
        share_link_type: campaign?.share_link_type ?? LINK_SHARE_LINK_TYPE,
        message_groups: campaign?.message_groups?.length ? campaign?.message_groups : [getDefaultMessageDummy()],
        multi_action_children,
        combined_campaigns:
            campaign?.combined_campaigns?.map((campaign_resource: string) =>
                DjangIO.app.grassroots.campaign.models.Campaign.idFromResourceUri(campaign_resource),
            ) ?? [],
        order: campaign?.order?.toString(),
        vanity_url_slug: campaign?.vanity_url_slug || campaign?.id.toString(),
        ui_custom_targets_enabled,
        limitedToTeams: limitedToTeamsIds,
        teamPermissioning: limitedToTeamsIds?.length > 0 ? TEAM_PERMISSION_LIMITED_TEAMS : TEAM_PERMISSION_EVERYONE,
        uses_default_member_list: usesDefaultMemberList,
        primary_targets_saved_searches: primaryTargetsSavedSearches,
        personalized_campaign_pages: campaign?._extra?.personalized_campaign_pages ?? {},
        should_rework_with_ai: Boolean(campaign?.should_rework_with_ai),
        ai_options: {
            length: campaign?.ai_options?.length ?? "short",
            tone: campaign?.ai_options?.tone ?? "friendly",
        },
    } as any
}

export const validateKeywordForCampaign = async (keyword: string, campaignId?: number) => {
    const { Campaign } = DjangIO.app.grassroots.campaign.models

    return await Campaign.objects.action("validate_campaign_keyword").update({ keyword, campaignId }).PATCH()
}

export const validatePlaceholdersForCampaign = async (placeholders: string, customQuestionsIds?: number[]) => {
    const { Campaign } = DjangIO.app.grassroots.campaign.models

    return await Campaign.objects
        .action("are_placeholders_valid_for_campaign")
        .update({ text: placeholders, custom_question_ids: customQuestionsIds })
        .PATCH()
}

export const fetchMessageVariations = async (
    subject: string,
    content: string,
    fixed_pre_content?: string,
    fixed_post_content?: string,
    variations = 1,
): Promise<CampaignMessageVariation[]> => {
    const { Campaign } = DjangIO.app.grassroots.campaign.models

    return await Campaign.objects
        .action("get_ai_message_suggestions")
        .update({
            subject,
            content,
            fixed_pre_content,
            fixed_post_content,
            variations,
        })
        .PATCH()
}

export const formatCampaignToSubmit = (values: CampaignFormValues) => {
    const backgroundStyleType = // This is required for backwards compatibility https://quorumanalytics.slack.com/archives/C044H4U0BV1/p1696945008044599
        Number(values.background_style_type) === ActionCenterBackgroundStyle.org_design.value
            ? null
            : Number(values.background_style_type)

    const messageGroups = values.message_groups?.map((message, messageIndex) => {
        const allVariations = [...message.message_variations, ...(message?.archived_variations || [])]
        return {
            ...message,
            index: messageIndex,
            message_variations: allVariations?.map((variation, variationIndex) => {
                return {
                    ...variation,
                    index: variationIndex,
                    ai_generation_type: variationIndex === 0 ? null : variation?.ai_generation_type,
                }
            }),
            standard_options_search: message.uses_default_member_list ? message.standard_options_search : null,
            custom_officials_searches: message.uses_default_member_list ? [] : message.custom_officials_searches,
            custom_email_targets: message.has_custom_email_targets ? message.custom_email_targets : [],
        }
    })

    const isCombinedCampaign =
        values.campaign_type === DjangIO.app.grassroots.campaign.types.CampaignType.combined_campaign.value

    const vanityUrlSlug = values.vanity_url_slug === values.id?.toString() ? "" : values.vanity_url_slug

    const limitedToTeams = values.teamPermissioning === TEAM_PERMISSION_LIMITED_TEAMS ? values.limitedToTeams : []

    return {
        action_center: ActionCenterSettings.resourceUriFromId(values.action_center_id),
        // Layout
        campaign_layout: values.campaign_layout,
        page_layout_type: values.page_layout_type,
        description_alignment_type: Number(values.description_alignment_type),
        background_style_type: backgroundStyleType,
        navigation_bar_type: Number(values.navigation_bar_type),
        logo_override_url: values.logo_override_url,
        navigation_bar_is_transparent: values.navigation_bar_is_transparent,
        full_bleed_header_video_url: values.full_bleed_header_video_url,
        full_bleed_header_image_url: values.full_bleed_header_image_url,
        full_bleed_header_image_contain: values.full_bleed_header_image_contain,
        full_bleed_header_type: Number(values.full_bleed_header_type),
        background_color_override: values.background_color_override,
        custom_css: values.custom_css,
        custom_javascript: values.custom_javascript,
        show_num_actions: values.show_num_actions,
        summary_display_option_type: Number(values.summary_display_option_type),
        vanity_url_slug: vanityUrlSlug,

        // Basics
        name: values.name,
        internal_only_name: values.internal_only_name,
        long_description: values.long_description,
        background_image_override_url: values.background_image_override_url,
        background_image: values.background_image,
        short_description: values.short_description,
        registration_page: GrassrootsRegistrationPage.resourceUriFromId(values.registration_page),
        custom_call_to_action_text: values.custom_call_to_action_text,
        custom_call_to_action_button_text: values.custom_call_to_action_button_text,
        participants: values.participants,
        participants_saved_searches: values.participants_saved_searches,
        multiple_actions_allowed: values.multiple_actions_allowed,
        is_one_click_registration_enabled: values.is_one_click_registration_enabled,
        gamification_points_mode: values.gamification_points_mode,
        custom_gamification_points: values.custom_gamification_points,
        limited_to_teams: limitedToTeams?.map((team) => Team.resourceUriFromId(team)),

        // Post Action
        thank_you_message: values.thank_you_message,
        custom_thank_you_javascript: values.custom_thank_you_javascript,
        success_redirection_id: values.success_redirection_id,
        success_redirection_url: values.success_redirection_url,
        social_title: values.social_title,
        social_description: values.social_description,
        social_media_platforms: values.social_media_platforms,
        social_share_text: values.social_share_text,
        social_image_url: values.social_image_url,

        // @ts-ignore
        email_template: values.email_template ? EmailTemplate.resourceUriFromId(values.email_template as number) : null,
        conf_email_subject: values.conf_email_subject,
        conf_email_from_name: values.conf_email_from_name,
        conf_email_from_address: `${values.confirmation_email_from_address_username}@${values.confirmation_email_from_address_domain}`,
        conf_email_reply_to_name: values.conf_email_reply_to_name,
        conf_email_reply_to_address: values.conf_email_reply_to_address,

        // Finalize
        projects: values.projects?.map((project: number) => Project.resourceUriFromId(project)),
        tagged_bills: values.tagged_bills?.map((bill: number) => Bill.resourceUriFromId(bill)),
        tagged_regulations: values.tagged_regulations?.map((regulation: number) =>
            Regulation.resourceUriFromId(regulation),
        ),
        text_keyword: values.text_keyword,
        inbound_texting_reply_message: values.inbound_texting_reply_message,
        order: parseInt(values.order),
        visible_on_action_center: values.visible_on_action_center,

        // Other
        // Add "status" field once we determine how to handle it scheduled campaigns
        campaign_type: values.campaign_type,
        status: values?.status,
        activated: values?.activated,

        // WRITE A LETTER
        // Basics
        single_body_for_all_messages: values.single_body_for_all_messages,
        require_confirmation: values.require_confirmation,
        supporters_can_choose_targets: isCombinedCampaign ? true : values.supporters_can_choose_targets,

        // Message
        custom_target: values.custom_target,
        uses_default_member_list: values.uses_default_member_list,
        default_targets_saved_search: values.default_targets_saved_search,
        draft_requirements: values.draft_requirements,
        pos_message: values.pos_message,
        primary_targets_saved_searches: values.primary_targets_saved_searches,
        targeting_type: values.targeting_type,
        targets_district_office: values.targets_district_office,
        message_groups: messageGroups,
        allow_all_supporters_to_target_all_targets: values.allow_all_supporters_to_target_all_targets,
        always_show_to_advocates: values.always_show_to_advocates,

        // CALL A NUMBER
        multi_messages: values.multi_messages,
        // Finalize
        policy_area: values.policy_area ? BillTerm.resourceUriFromId(values.policy_area) : null,

        // COMMENT ON REGULATION
        regulation_id: values.regulation_id,
        custom_docket_link: values.custom_docket_link,
        submitting_organization_name: values.submitting_organization_name,
        comment_organization_type: values.comment_organization_type,

        // WRITE TO THE WHITE HOUSE
        pos_subject: values.pos_subject,

        fixed_pre_message:
            values.draft_requirements === DraftOptions.partial_edit.value ? values.fixed_pre_message || "" : "",
        pos_content: values.pos_content,
        fixed_post_message:
            values.draft_requirements === DraftOptions.partial_edit.value ? values.fixed_post_message || "" : "",

        // SHARE A LINK
        share_url: values.share_url,
        share_campaign_link: values.share_campaign_link,

        // SHARE A STORY
        share_story_type: values.share_story_type,
        share_story_video_duration: values.share_story_video_duration,
        video_notified_users: values.video_notified_users,

        //LOG INTERACTION
        interaction_types: values.interaction_types,
        custom_fields: values.custom_fields,

        // LOG RELATIONSHIP
        relationship_types: values.relationship_types,

        // COMBINED CAMPAIGNS
        combined_campaigns: values.combined_campaigns?.map((campaignId) =>
            DjangIO.app.grassroots.campaign.models.Campaign.resourceUriFromId(campaignId),
        ),

        // MULTI ACTION
        restrictions: values?.restrictions,
        multi_action_children: values?.multi_action_children?.map((child) => ({
            ...formatCampaignToSubmit(child),
            multi_action_parent_id: values.id,
        })),
        multi_action_parent_id: null,
        multi_action_ordered_children_ids: values.multi_action_ordered_children_ids ?? [],
        id: values.id,

        // PERSONALIZED MESSAGES
        // custom_email_targets: values.custom_email_targets,
        // has_custom_email_targets: values?.has_custom_email_targets,
        personalized_campaign_pages: Object.keys(values.personalized_campaign_pages).reduce((acc, key) => {
            acc[key] = values.personalized_campaign_pages[key].map((field) => field.id)
            return acc
        }, {}),

        // AI
        ...(isFeatureEnabled("ff_ngg_rework_interactive_by_ai")
            ? { should_rework_with_ai: Boolean(values?.should_rework_with_ai), ai_options: values?.ai_options }
            : {}),
    }
}

export const generatePlaceholdersForPreview = async ({
    message,
    tone,
    length,
}: {
    message: string
    tone: string
    length: string
}) => {
    const response = await api.patch(
        { model: DjangIO.app.grassroots.campaign.models.Campaign, params: "get_ai_message_preview=true" },
        { message, tone, length },
        { throwOnNotOk: false },
    )
    return (await response?.json()) as Record<string, string>
}

export const useMutateCampaign = (campaignId?: string | number) =>
    useMutation({
        mutationFn: (values: CampaignFormValues) => {
            const data = formatCampaignToSubmit({ ...values, id: campaignId as number })

            // If we have a campaignId, we're updating an existing campaign
            if (campaignId) {
                return DjangIO.app.grassroots.campaign.models.Campaign.objects.get(campaignId).update(data).PATCH()
            }

            // Otherwise, we're creating a new campaign
            return DjangIO.app.grassroots.campaign.models.Campaign.objects.create(data).POST()
        },
    })

export const updateCampaignStatus = (campaignId: number, payload: unknown) => {
    return DjangIO.app.grassroots.campaign.models.Campaign.objects.get(campaignId).update(payload).PATCH()
}

const fetchPlaceholders = async (): Promise<Placeholder[]> => {
    return await CampaignModel.objects.action("get_placeholders_for_campaign").GET()
}

export const useCampaignPlaceholders = () =>
    useQuery({
        queryKey: ["campaignPlaceholders"],
        queryFn: fetchPlaceholders,
        select: useCallback((data) => data ?? [], []),
        staleTime: Infinity,
    })

export const createSafedSearchObject = async (
    { searchifyFilters },
    data_type: QuorumDataTypesWithValue,
    name: string,
) => {
    const kwargs = {
        user_id: Userdata.id,
        data_type: data_type.value,
        filters: searchifyFilters["querysetList"],
        added_ids: searchifyFilters["addedIds"],
        deleted_ids: searchifyFilters["deletedIds"],
        name,
    }
    const response = await api.post(
        { model: DjangIO.app.userdata.models.SafedSearch, params: "decode_enums=false&count=false&exclude=%7B%7D" },
        kwargs,
        { throwOnNotOk: false },
    )
    return (await response?.json()) as SafedSearch
}

const fetchSavedQuestions = async () => {
    const response = await api.get({
        model: DjangIO.app.grassroots.models.CustomTag,
        action: "get_personalized_questions",
    })

    return response.json()
}

export const useSavedQuestions = () =>
    useQuery({
        queryKey: ["savedQuestions"],
        queryFn: fetchSavedQuestions,
        staleTime: Infinity,
    })

export const useAddSavedQuestion = () => {
    const queryClient = useQueryClient()

    const addSavedQuestion = (newQuestion: CustomField) => {
        queryClient.setQueryData<CustomField[]>(["savedQuestions"], (createdQuestions) => {
            if (createdQuestions) {
                return [...createdQuestions, newQuestion]
            }
            return [newQuestion]
        })
    }

    return addSavedQuestion
}
